/**
 * CampaignDialogs - Create and Edit campaign dialogs
 */
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CAMPAIGN_THEME_OPTIONS, getThemeByKey } from './campaignUtils';

export function CreateCampaignDialog({
  open,
  onOpenChange,
  campaignForm,
  onFormChange,
  onSubmit,
  saving
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create New Campaign</DialogTitle>
          <DialogDescription>
            Create a marketing campaign with auto-generated URL and QR code
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label className="text-sm font-medium text-foreground mb-1.5 block">
              Campaign Title *
            </Label>
            <Input
              value={campaignForm.title}
              onChange={(e) => onFormChange({ ...campaignForm, title: e.target.value })}
              placeholder="e.g., Summer Special Promotion"
              data-testid="campaign-title-input"
            />
          </div>
          
          <div>
            <Label className="text-sm font-medium text-foreground mb-1.5 block">
              Description *
            </Label>
            <Textarea
              value={campaignForm.description}
              onChange={(e) => onFormChange({ ...campaignForm, description: e.target.value })}
              placeholder="Describe what this campaign offers..."
              rows={3}
              data-testid="campaign-description-input"
            />
          </div>
          
          <div>
            <Label className="text-sm font-medium text-foreground mb-1.5 block">
              Footer Text (optional)
            </Label>
            <Input
              value={campaignForm.footer}
              onChange={(e) => onFormChange({ ...campaignForm, footer: e.target.value })}
              placeholder="e.g., Limited time offer"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-foreground mb-1.5 block">
                Theme Color
              </Label>
              <Select
                value={campaignForm.theme}
                onValueChange={(value) => onFormChange({ ...campaignForm, theme: value })}
              >
                <SelectTrigger data-testid="campaign-theme-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_THEME_OPTIONS.map((theme) => (
                    <SelectItem key={theme.key} value={theme.key}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: theme.color }}
                        />
                        {theme.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm font-medium text-foreground mb-1.5 block">
                Discount %
              </Label>
              <Input
                type="number"
                min={0}
                max={50}
                value={campaignForm.discount_percent}
                onChange={(e) => onFormChange({ ...campaignForm, discount_percent: parseInt(e.target.value) || 0 })}
                data-testid="campaign-discount-input"
              />
            </div>
          </div>

          {/* Preview */}
          <div className="p-4 bg-muted/30 rounded-lg">
            <p className="text-xs text-muted-foreground mb-2">Preview</p>
            <div className="flex items-center gap-3">
              <div 
                className="w-3 h-12 rounded-full"
                style={{ backgroundColor: getThemeByKey(campaignForm.theme).color }}
              />
              <div>
                <p className="font-medium text-foreground">{campaignForm.title || 'Campaign Title'}</p>
                <p className="text-sm text-muted-foreground">{campaignForm.discount_percent}% discount</p>
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={saving} data-testid="save-campaign-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create Campaign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditCampaignDialog({
  open,
  onOpenChange,
  editingCampaign,
  campaignForm,
  onFormChange,
  onSubmit,
  saving
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Campaign {editingCampaign?.campaign_id}</DialogTitle>
          <DialogDescription>
            Update campaign details
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label className="text-sm font-medium text-foreground mb-1.5 block">
              Campaign Title *
            </Label>
            <Input
              value={campaignForm.title}
              onChange={(e) => onFormChange({ ...campaignForm, title: e.target.value })}
              placeholder="e.g., Summer Special Promotion"
            />
          </div>
          
          <div>
            <Label className="text-sm font-medium text-foreground mb-1.5 block">
              Description *
            </Label>
            <Textarea
              value={campaignForm.description}
              onChange={(e) => onFormChange({ ...campaignForm, description: e.target.value })}
              placeholder="Describe what this campaign offers..."
              rows={3}
            />
          </div>
          
          <div>
            <Label className="text-sm font-medium text-foreground mb-1.5 block">
              Footer Text (optional)
            </Label>
            <Input
              value={campaignForm.footer}
              onChange={(e) => onFormChange({ ...campaignForm, footer: e.target.value })}
              placeholder="e.g., Limited time offer"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-foreground mb-1.5 block">
                Theme Color
              </Label>
              <Select
                value={campaignForm.theme}
                onValueChange={(value) => onFormChange({ ...campaignForm, theme: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_THEME_OPTIONS.map((theme) => (
                    <SelectItem key={theme.key} value={theme.key}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: theme.color }}
                        />
                        {theme.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm font-medium text-foreground mb-1.5 block">
                Discount %
              </Label>
              <Input
                type="number"
                min={0}
                max={50}
                value={campaignForm.discount_percent}
                onChange={(e) => onFormChange({ ...campaignForm, discount_percent: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
